import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { THEME } from 'GlobalStyles';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { AttachmentClipIcon, SvgWarning } from 'UI/res/';
import { colors } from 'UI/res/colors';

import FPChip from '../FPChip';
import FPIcon from '../FPIcon';
import { When } from '../When';

import {
  SEQUENCE_CHIP_COLOR,
  SEQUENCE_LIST_STATUSES_COLORS_BY_ID
} from './EmailItemCard.constants';
import { useStyles } from './styles';

const { list: listCopies } = strings.bulkEmails.content;

/**
 * @typedef {Object} BulkSequenceStatus
 * @property {number} id - Unique identifier for the status state.
 * @property {string} title - The display name of the status (e.g., "In Course").
 */

/**
 * @typedef {Object} BulkSequence
 * @property {number} id - Unique identifier for the sequence item record.
 * @property {number} origin_email_history_id - Foreign key linking to the original email history entry.
 * @property {number} bulk_sequence_status_id - Foreign key representing the current status ID.
 * @property {null|string|number} stopped_by - Identifies who or what process halted the sequence. `null` if still active.
 * @property {null|string} stopped_at - ISO timestamp or date indicating when the sequence was stopped. `null` if still active.
 * @property {BulkSequenceStatus} status - Nested object containing detailed status metadata.
 */

/**
 * @typedef {Object} EmailItemCardProps
 * @property {string} bulkId - Unique identifier for the bulk email or batch dispatch.
 * @property {string} formattedDate - The display-ready formatted date of the email.
 * @property {boolean} hasAttachments - Indicates whether the email contains file attachments.
 * @property {boolean} isActiveCard - Determines if the card is currently selected or focused.
 * @property {function(): number} onSelectEmail - Callback function triggered when the card is selected. Returns a number.
 * @property {string} previewText - Snippet or preview text of the email body content.
 * @property {string} searchProjectName - Name of the associated project used for searching or filtering.
 * @property {BulkSequence} [sequence = null] - Sequence object containing values for item to be displayed correctly
 * @property {boolean} shouldShowAILabel - Determines if the AI-generated label should be displayed.
 * @property {string} subject - The subject line of the email.
 * @property {string} [warning=''] - Optional warning message. Defaults to an empty string.
 */

/**
 * Component that renders an email preview card within a list layout.
 *
 * @param {EmailItemCardProps} props - The component properties.
 * @returns {JSX.Element} The rendered JSX element for the card.
 */
const EmailItemCard = ({
  bulkId,
  formattedDate,
  hasAttachments,
  isActiveCard,
  onSelectEmail,
  previewText,
  searchProjectName,
  sequence,
  shouldShowAILabel,
  subject,
  warning = ''
}) => {
  const classes = useStyles({ hasAttachments, hasWarning: !!warning });
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isSequencesFeatureEnabled = checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailSequences);
  const isBulkSequence = !!sequence;

  const handleActiveCard = selectedEmailId => () => onSelectEmail && onSelectEmail(selectedEmailId);

  return bulkId ? (
    <Grid
      data-testid="email-item"
      container
      justify="center"
      direction="column"
      wrap="nowrap"
      className={clsx(classes.emailItem, isActiveCard && classes.activeCard)}
      onClick={handleActiveCard(bulkId)}
    >
      <Grid item container justify="space-between">
        <Typography noWrap variant="subtitle1" className={classes.emailSearchProjectName}>
          {searchProjectName}
        </Typography>
        <Typography variant="caption" className={classes.emailDate}>
          {formattedDate}
        </Typography>
      </Grid>
      <Grid item container justify="space-between">
        <Typography noWrap className={classes.previewText} variant="body2">
          {subject}
        </Typography>
        {hasAttachments && <AttachmentClipIcon size={15} fill={colors.darkGrey} />}
      </Grid>
      {!warning && (
        <Typography noWrap className={classes.previewText} variant="body2">
          {previewText}
        </Typography>
      )}
      <When condition={shouldShowAILabel}>
        <Typography variant="body2" component="span" className={classes.aiText}>
          {listCopies.aiLabel}
        </Typography>
      </When>
      <When condition={isBulkSequence && isSequencesFeatureEnabled}>
        <Grid container item className={classes.chipSpacing} spacing={1}>
          <Grid item>
            <FPChip size="small" color={SEQUENCE_CHIP_COLOR} label={listCopies.chips.sequence} />
          </Grid>
          <Grid item>
            <FPChip
              size="small"
              color={
                SEQUENCE_LIST_STATUSES_COLORS_BY_ID[sequence?.status?.id] ||
                SEQUENCE_LIST_STATUSES_COLORS_BY_ID.defualt
              }
              label={sequence?.status?.title}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </When>
      {warning && (
        <Grid item container alignItems="center" wrap="nowrap" className={classes.warningContainer}>
          <FPIcon size={18} color={THEME.palette.error.main} className={classes.warningIcon}>
            <SvgWarning />
          </FPIcon>
          <Typography variant="body1">{warning}</Typography>
        </Grid>
      )}
    </Grid>
  ) : null;
};

export default EmailItemCard;
